import React, { useEffect, useRef } from 'react'
import { Combobox, InputBase, useCombobox } from '@mantine/core'
import styled from 'styled-components'

import { Icon } from '../icon'
import { Loader } from '../loader'
import { SearchInput } from '../search-input'
import { Spinner } from '../spinner'
import { Tooltip } from '../tooltip'
import { FilterMultiOption, FilterMultiProps } from './filter-multi.types'

const ALL_VALUE = '__filter-multi-all__'

const Wrapper = styled.div`
  position: relative;
  display: inline-block;

  .mantine-InputBase-wrapper {
    background-color: white !important;
    width: fit-content;
  }

  .mantine-InputBase-input {
    border: none;
    background-color: white;
    border-radius: ${({ theme }) => theme.borderRadii.large};
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    padding: 0 ${({ theme }) => theme.sizes.xs};
    height: auto;
    width: fit-content;
    color: black;
    font-size: ${({ theme }) => theme.fontSizes.default};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    user-select: none;

    *,
    span,
    svg {
      cursor: pointer;
      user-select: none;
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.gray70};
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  .mantine-Combobox-option {
    padding: 0;
    word-break: normal;
    cursor: pointer;
  }

  .mantine-Combobox-options {
    padding: ${({ theme }) => theme.sizes.xxs};
    gap: 0;
    max-height: min(60vh, 320px);
    overflow-y: auto;
  }

  .mantine-Combobox-dropdown {
    box-shadow: ${({ theme }) => theme.boxShadows.small};
    background-color: white;
    width: auto;
    min-width: fit-content;
    max-width: fit-content;
  }
`

// The trigger sizes to its label, so a fit-content dropdown can be narrower than
// its own search box. A floor fixes that; the width is otherwise left to size to
// the options exactly as the plain checklist does, since rows never wrap.
const SearchableWrapper = styled(Wrapper)`
  .mantine-Combobox-dropdown {
    min-width: 17rem;
  }
`

const TriggerRow = styled.span`
  display: inline-flex;
  align-items: center;
`

const TriggerLabel = styled.span`
  display: inline-block;
`

// The slot the selection-count badge sits in. With `$reserve`, it keeps a fixed
// 18px footprint even when empty so selecting a value doesn't widen the trigger
// and reflow the filters beside it — the space is reserved up front (NEX-754).
// Without it, the slot sizes to its content and is only rendered once selected,
// matching the default (space-on-demand) behaviour.
const BadgeSlot = styled.span<{ $reserve: boolean }>`
  ${({ theme, $reserve }) => `
    margin-left: ${theme.sizes.xxs};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${$reserve ? 'width: 18px; height: 18px;' : ''}
  `}
`

const SelectionBadge = styled.span`
  ${({ theme }) => `
    font-size: ${theme.fontSizes.buttonText};
    font-weight: ${theme.fontWeights.semiBold};
    background: ${theme.colors.primary400};
    color: ${theme.colors.white};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  `}
`

const IconWrapper = styled.span`
  ${({ theme }) => `
  display: inline-flex;
  align-items: center;
  padding-left: ${theme.sizes.xxs};

  svg {
    width: 1rem;
    height: 1rem;
  }
  `}
`

const OptionRow = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.sizes.xs};
    padding: 6px ${theme.sizes.xs};
    border-radius: ${theme.borderRadii.medium};
    font-size: ${theme.fontSizes.default};
    color: ${theme.colors.black};
    cursor: pointer;
    white-space: nowrap;
    user-select: none;

    &:hover {
      background: ${theme.colors.gray70};
    }
  `}
`

const Checkbox = styled.span<{ $checked: boolean }>`
  ${({ theme, $checked }) => `
    width: ${theme.sizes[4]};
    height: ${theme.sizes[4]};
    border-radius: ${theme.borderRadii.small};
    border: 1px solid ${
      $checked ? theme.colors.primary400 : theme.colors.gray100
    };
    background: ${$checked ? theme.colors.primary400 : 'transparent'};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `}
`

const CenteredLoader = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.xs};
    padding: ${theme.spacing.s} ${theme.spacing.m};
    font-size: ${theme.fontSizes.default};
    color: ${theme.semantic.contentSecondary};
  `}
`

const Divider = styled.hr`
  ${({ theme }) => `
    border: none;
    border-top: 1px solid ${theme.colors.gray90};
    margin: ${theme.sizes.xxs} 0;
  `}
`

// Sits above the scrolling option list, so the search box stays put while the
// results scroll under it.
const SearchSlot = styled.div`
  ${({ theme }) => `
    padding: ${theme.spacing.xxs} ${theme.spacing.xxs} 0;
  `}
`

const EmptyState = styled.div`
  ${({ theme }) => `
    padding: ${theme.spacing.xs};
    font-size: ${theme.fontSizes.default};
    color: ${theme.semantic.contentSecondary};
  `}
`

// Padding matches OptionRow, so the row sits in the same rhythm as the options
// it follows.
const LoadMoreRow = styled.button`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.xs};
    width: 100%;
    padding: 6px ${theme.spacing.xs};
    border: none;
    background: none;
    border-radius: ${theme.radii.interactive};
    font-size: ${theme.fontSizes.default};
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.semantic.contentBrand};
    cursor: pointer;

    &:hover {
      background: ${theme.semantic.backgroundHover};
    }

    &:disabled {
      cursor: default;
      color: ${theme.semantic.contentSecondary};
    }
  `}
`

export const FilterMulti = ({
  label,
  options,
  selectedValues,
  onChange,
  isDisabled,
  tooltipContent,
  isLoading,
  testId,
  showAllOption = true,
  allOptionLabel = 'All',
  position = 'bottom-end',
  withinPortal = false,
  reserveBadgeSpace = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  clearSearchAriaLabel = 'Clear search',
  noResultsText = 'No results found',
  noOptionsText,
  loadingText,
  selectedOptions,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  loadMoreText = 'Load more',
  onOpenChange,
}: FilterMultiProps) => {
  const combobox = useCombobox({
    onDropdownOpen: () => onOpenChange?.(true),
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      onOpenChange?.(false)
    },
  })
  const open = combobox.dropdownOpened

  const toggleValue = (value: string) => {
    if (value === ALL_VALUE) {
      onChange([])

      return
    }
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value]
    onChange(next)
  }

  const handleToggleOpen = () => {
    if (isDisabled) return
    combobox.toggleDropdown()
  }

  // Searching hides options, so the selection is listed in its own section above
  // the results — always visible, always untickable. Without a search box the
  // list is short and complete, so options keep their natural position and
  // ticking one doesn't make it jump.
  const isSearchable = Boolean(onSearchChange)
  const optionByValue = new Map(
    [...options, ...(selectedOptions ?? [])].map((option) => [
      option.value,
      option,
    ])
  )
  const selectedSet = new Set(selectedValues)
  const selectedRows = isSearchable
    ? selectedValues.map(
        (value) => optionByValue.get(value) ?? { value, label: value }
      )
    : []
  const resultRows = isSearchable
    ? options.filter((option) => !selectedSet.has(option.value))
    : options

  // Scrolling fires far faster than `isLoadingMore` can come back as a prop, so a
  // flick past the threshold would ask for the same page several times. The ref
  // latches synchronously on the first ask and clears once the page has landed.
  const loadMoreAskedRef = useRef(false)

  // `isLoadingMore` is optional, so a caller can page without ever reporting the
  // fetch: for them the page landing shows up only as more options, and without
  // that dep the latch would never clear and paging would stop after one page.
  useEffect(() => {
    if (!isLoadingMore) loadMoreAskedRef.current = false
  }, [isLoadingMore, options.length])

  // An empty result list isn't an empty dropdown: it also happens when every
  // option is ticked, or when a search matches only ticked ones — both still
  // list those above. So the empty state keys off `options`, and only claims the
  // filter has nothing at all when there's no selection to show either.
  const emptyStateText = (() => {
    if (!isSearchable || options.length > 0) return undefined
    if (searchValue) return noResultsText

    return selectedRows.length === 0 ? noOptionsText : undefined
  })()

  // Every route to the next page goes through here, so a click and the scroll it
  // rides on can't both ask for it.
  const askForNextPage = () => {
    if (!onLoadMore || loadMoreAskedRef.current) return
    loadMoreAskedRef.current = true
    onLoadMore()
  }

  // Fetch the next page as the user nears the bottom; the "Load more" row stays
  // as the keyboard / no-scroll fallback.
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoadingMore) return

    const el = event.currentTarget

    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 48) askForNextPage()
  }

  const renderRow = ({
    value,
    label,
    checked,
    rowTestId,
  }: FilterMultiOption & { checked: boolean; rowTestId: string }) => (
    <Combobox.Option key={value} value={value} active={checked}>
      <OptionRow data-testid={rowTestId}>
        <Checkbox $checked={checked} aria-hidden>
          {checked && <Icon name="check" size={5} color="white" />}
        </Checkbox>
        <span>{label}</span>
      </OptionRow>
    </Combobox.Option>
  )

  const renderOption = ({ value, label }: FilterMultiOption) =>
    renderRow({
      value,
      label,
      checked: selectedSet.has(value),
      rowTestId: `${testId}-option-${value}`,
    })

  const trigger = (
    <Combobox.Target>
      <InputBase
        component="button"
        type="button"
        onClick={handleToggleOpen}
        rightSectionPointerEvents="none"
        data-testid={testId}
        disabled={isDisabled}
      >
        <TriggerRow>
          <TriggerLabel>{label}</TriggerLabel>
          {reserveBadgeSpace ? (
            <BadgeSlot
              $reserve
              data-testid={testId ? `${testId}-badge-slot` : undefined}
            >
              {selectedValues.length > 0 && (
                <SelectionBadge data-testid={`${testId}-badge`}>
                  {selectedValues.length}
                </SelectionBadge>
              )}
            </BadgeSlot>
          ) : (
            selectedValues.length > 0 && (
              <BadgeSlot $reserve={false}>
                <SelectionBadge data-testid={`${testId}-badge`}>
                  {selectedValues.length}
                </SelectionBadge>
              </BadgeSlot>
            )
          )}
          <IconWrapper>
            <Icon
              name={open ? 'chevron-up' : 'chevron-down'}
              size={8}
              color="black"
            />
          </IconWrapper>
        </TriggerRow>
      </InputBase>
    </Combobox.Target>
  )

  const DropdownWrapper = isSearchable ? SearchableWrapper : Wrapper

  return (
    <DropdownWrapper>
      <Combobox
        store={combobox}
        withinPortal={withinPortal}
        position={position}
        onOptionSubmit={toggleValue}
      >
        {isDisabled && tooltipContent ? (
          <Tooltip content={tooltipContent}>{trigger}</Tooltip>
        ) : (
          trigger
        )}
        <Combobox.Dropdown>
          {isSearchable && (
            <SearchSlot>
              <SearchInput
                value={searchValue}
                onChange={(event) => onSearchChange?.(event.target.value)}
                handleClearValue={() => onSearchChange?.('')}
                placeholder={searchPlaceholder}
                clearButtonAriaLabel={clearSearchAriaLabel}
                inputSize="small"
                autoComplete="off"
                data-1p-ignore
              />
            </SearchSlot>
          )}
          <Combobox.Options onScroll={handleScroll}>
            {isLoading ? (
              <CenteredLoader>
                <Loader />
                {loadingText && <span>{loadingText}</span>}
              </CenteredLoader>
            ) : (
              <>
                {showAllOption && (
                  <>
                    {renderRow({
                      value: ALL_VALUE,
                      label: allOptionLabel,
                      checked: selectedValues.length === 0,
                      rowTestId: `${testId}-option-all`,
                    })}
                    <Divider />
                  </>
                )}
                {selectedRows.length > 0 && (
                  <div data-testid={`${testId}-selected`}>
                    {selectedRows.map(renderOption)}
                    <Divider />
                  </div>
                )}
                {resultRows.map(renderOption)}
                {emptyStateText && (
                  <EmptyState data-testid={`${testId}-empty`}>
                    {emptyStateText}
                  </EmptyState>
                )}
                {hasMore && onLoadMore && (
                  <LoadMoreRow
                    type="button"
                    onClick={askForNextPage}
                    disabled={isLoadingMore}
                    data-testid={`${testId}-load-more`}
                  >
                    {isLoadingMore && <Spinner size="xs" color="primary" />}
                    {loadMoreText}
                  </LoadMoreRow>
                )}
              </>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </DropdownWrapper>
  )
}
